import { type ReactNode, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/shared/utils/cn';

import { XIcon } from './icons';

interface DialogProps {
  /** The title, read as the dialog's name. */
  title: ReactNode;
  /** One line under the title, in the quiet ink. */
  description?: ReactNode;
  /** Called on Escape, on the «×» and on a click outside. The parent decides what closing means. */
  onClose: () => void;
  /** `sm` for a question, `md` for a form, `lg` for a wide one. */
  size?: 'sm' | 'md' | 'lg';
  /** The buttons, aligned to the right under the body. */
  footer?: ReactNode;
  children: ReactNode;
}

const SIZES: Record<NonNullable<DialogProps['size']>, string> = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-xl',
  lg: 'sm:max-w-3xl',
};

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * A modal sheet over the screen: one thing to do, the rest waits.
 *
 * Only the chrome lives here (backdrop, title, «×», Escape, the focus): what the sheet
 * asks and what the buttons do is the caller's business, so a confirmation and a form
 * are the same component with different children. Mounted means open: the parent keeps
 * the state and renders it or not, which is how every other conditional block of the
 * app already works.
 *
 * The focus goes to the first field (or the first button) when it opens and back to
 * whatever had it when it closes, and Tab stays inside while it is up: a modal that lets
 * the focus wander to the page behind it is a modal only for the eyes. On a phone it
 * rises from the bottom, like the «Más» sheet of the navigation.
 */
export function Dialog({
  title,
  description,
  onClose,
  size = 'md',
  footer,
  children,
}: DialogProps) {
  const titleId = useId();
  const descriptionId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    // `data-autofocus` names the element that should take the focus (the answer of a
    // confirmation); otherwise it is the first field, or the first button that is not «×».
    const first =
      panel.current?.querySelector<HTMLElement>('[data-autofocus]') ??
      panel.current?.querySelector<HTMLElement>(
        'input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]):not([data-dialog-close])',
      );
    (first ?? panel.current)?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        close.current();
        return;
      }
      if (event.key !== 'Tab' || !panel.current) return;
      const focusable = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      const firstEl = focusable[0];
      const lastEl = focusable[focusable.length - 1];
      if (!firstEl || !lastEl) return;
      if (event.shiftKey && document.activeElement === firstEl) {
        event.preventDefault();
        lastEl.focus();
      } else if (!event.shiftKey && document.activeElement === lastEl) {
        event.preventDefault();
        firstEl.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      opener?.focus?.();
    };
  }, []);

  // Painted on `body`, not where it was called from: a sheet opened from a table cell
  // would otherwise inherit its `white-space: nowrap` and `text-align: right` (fixed
  // positioning escapes the layout, not the inherited text styles) and its z-index would
  // fight the scroll region of the table. The reset below is the belt to those braces.
  return createPortal(
    <div
      className="bg-ink/40 fixed inset-0 z-50 flex items-end justify-center text-left font-normal whitespace-normal sm:items-center sm:p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cn(
          'bg-card border-line flex max-h-[calc(100dvh-2rem)] w-full flex-col rounded-t-2xl border shadow-lg outline-none sm:rounded-lg',
          SIZES[size],
        )}
      >
        <header className="border-line-2 flex items-start justify-between gap-3 border-b px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="font-display text-ink text-[19px] leading-6 font-semibold">
              {title}
            </h2>
            {description ? (
              <p id={descriptionId} className="text-ink-3 mt-1 text-[13.5px] leading-snug">
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            data-dialog-close
            aria-label="Cerrar"
            onClick={onClose}
            className="text-ink-3 hover:bg-paper-2 hover:text-ink focus-visible:outline-accent -mr-2 inline-flex size-9 shrink-0 items-center justify-center rounded-md transition-colors focus-visible:outline focus-visible:outline-2"
          >
            <XIcon size={18} />
          </button>
        </header>
        <div className="flex flex-col gap-3 overflow-y-auto px-5 py-4">{children}</div>
        {footer ? (
          <footer className="border-line-2 flex flex-wrap items-center justify-end gap-2 border-t px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
